import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import type { AuthUserOnboarding } from "../../types/auth";
import SignupShell from "./SignupShell";
import CreateAccountStep from "./CreateAccountStep";
import ConnectAccountingStep from "./ConnectAccountingStep";
import AddPaymentStep from "./AddPaymentStep";
import CustomizeAIStep from "./CustomizeAIStep";

const totalSteps = 4;

const stepMeta = [
  { title: "Create Account", subtitle: "Join Quantira.ai to get started with AI-powered financial insights" },
  { title: "Connect Your Accounting", subtitle: "Connect your accounting software to unlock powerful financial insights" },
  { title: "Add Payment Method", subtitle: "Start your 1-day free trial - no charges until after trial period" },
  { title: "Customize AI Insights", subtitle: "Tell us about your business to get personalized AI insights" },
];

const inferStepFromOnboarding = (onboarding: AuthUserOnboarding | null | undefined): number => {
  if (!onboarding) return 2;
  if (!onboarding.accountingPlatform && !onboarding.accountingSkipped) return 2;
  if (!onboarding.plan) return 3;
  if (!onboarding.completedAt) return 4;
  return totalSteps + 1;
};

export default () => {
  const { session, isLoading, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [onboarding, setOnboarding] = useState<AuthUserOnboarding | null>(null);
  const [isStepResolving, setIsStepResolving] = useState(false);
  // The regular /signup flow is for users creating their own company. If a stale
  // invite cookie is present from a prior visit to /signup/accept-invite, drop it
  // so that an OAuth round-trip from this page can't accidentally pull the user
  // into the inviter's company.
  useEffect(() => {
    void fetch("/api/auth/invite/clear", { method: "POST" });
  }, []);

  useEffect(() => {
    if (!session) {
      setStep(1);
      return;
    }

    let cancelled = false;
    setIsStepResolving(true);
    void fetch("/api/onboarding/status")
      .then(async response => {
        if (!response.ok) return null;
        const result = await response.json().catch(() => null) as { onboarding?: AuthUserOnboarding } | null;
        return result?.onboarding ?? null;
      })
      .then(value => {
        if (cancelled) return;
        setOnboarding(value);
        const nextStep = inferStepFromOnboarding(value);
        if (nextStep > totalSteps) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setStep(nextStep);
      })
      .finally(() => {
        if (!cancelled) setIsStepResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session, navigate]);

  if (isLoading || isStepResolving) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm font-medium text-slate-500">Loading...</div>;
  }

  if (session && onboarding?.completedAt) {
    return <Navigate to="/dashboard" replace />;
  }

  const meta = stepMeta[step - 1] ?? stepMeta[0]!;

  const handleStep1Complete = async () => {
    await refreshSession();
    setStep(2);
  };

  const handleStep2Complete = async () => {
    setStep(3);
  };

  const handleStep3Complete = async () => {
    setStep(4);
  };

  const handleStep4Complete = async () => {
    await refreshSession();
    navigate("/dashboard", { replace: true });
  };

  return (
    <SignupShell step={step} totalSteps={totalSteps} title={meta.title} subtitle={meta.subtitle}>
      {step === 1 && <CreateAccountStep onComplete={handleStep1Complete} />}
      {step === 2 && <ConnectAccountingStep onComplete={handleStep2Complete} />}
      {step === 3 && <AddPaymentStep defaultPlan={onboarding?.plan} onComplete={handleStep3Complete} onBack={() => setStep(2)} />}
      {step === 4 && <CustomizeAIStep defaultValues={onboarding?.aiPreferences} onComplete={handleStep4Complete} />}
    </SignupShell>
  );
};
